import { useI18n } from 'vue-i18n'
import { useSnackbarStore } from '@/stores/snackbar'

interface MessageError {
  type: 'message'
  message: string
}

interface FieldError {
  type: 'field'
  field: string
  rules: string[]
}

interface ErrorResponse {
  errors: (MessageError | FieldError)[]
}

export const useErrorHandler = () => {
  const { t } = useI18n()
  const snackbarStore = useSnackbarStore()

  // Field rule mapping for localization
  const ruleMessages: Record<string, string> = {
    not_found: 'errors.field.not_found',
    required: 'errors.field.required',
    too_long: 'errors.field.too_long',
    min_length_3: 'errors.field.min_length_3',
  }

  // Field name mapping for localization
  const fieldMessages: Record<string, string> = {
    email: 'fields.email',
    password: 'fields.password',
    name: 'fields.name',
    phone: 'fields.phone',
    slug: 'fields.slug',
    // Diğer field'lar gerektiğinde eklenebilir
  }

  const getFieldMessage = (field: string): string => {
    return fieldMessages[field] ? t(fieldMessages[field]) : field
  }

  const getRuleMessage = (rule: string, field?: string): string => {
    const fieldName = field ? getFieldMessage(field) : ''
    const ruleKey = ruleMessages[rule]
    
    if (ruleKey) {
      return t(ruleKey, { field: fieldName })
    }
    
    return `${fieldName} ${rule}` // Fallback
  }

  const handleMessageError = (error: MessageError) => {
    // Snackbar ile hata mesajını göster
    if (process.client) {
      snackbarStore.setSnackbar({
        severity: 'danger',
        title: {
          langkey: error.message,
          params: {},
        },
        timeout: 5000,
        persistent: false,
      })
    }
  }

  const handleFieldError = (error: FieldError): Record<string, string> => {
    const fieldErrors: Record<string, string> = {}
    
    // İlk rule'u kullan (birden fazla rule varsa)
    const firstRule = error.rules[0]
    if (firstRule) {
      fieldErrors[error.field] = getRuleMessage(firstRule, error.field)
      
      // Field error'ları için de snackbar göster
      if (process.client) {
        snackbarStore.setSnackbar({
          severity: 'danger',
          title: {
            langkey: getRuleMessage(firstRule, error.field),
            params: {},
          },
          timeout: 5000,
          persistent: false,
        })
      }
    }
    
    return fieldErrors
  }

  const handle500Error = () => {
    // Snackbar ile server error göster
    if (process.client) {
      snackbarStore.setSnackbar({
        severity: 'danger',
        title: {
          langkey: 'errors.server.internal_error',
          params: {},
        },
        timeout: 5000,
        persistent: false,
      })
    }
  }

  const handleApiError = (errorResponse: ErrorResponse | any): Record<string, string> => {
    const fieldErrors: Record<string, string> = {}

    if (!errorResponse?.errors) {
      // 500 or unexpected error
      handle500Error()
      return fieldErrors
    }

    errorResponse.errors.forEach((error: MessageError | FieldError) => {
      if (error.type === 'message') {
        handleMessageError(error)
      } else if (error.type === 'field') {
        const fieldError = handleFieldError(error)
        Object.assign(fieldErrors, fieldError)
      }
    })

    return fieldErrors
  }

  const handleHttpError = (error: any): Record<string, string> => {
    // Mande API error structure kontrolü
    if (error?.body) {
      // Mande error'unda body içinde response var
      return handleApiError(error.body)
    }
    
    // Mande error message kontrolü (404 gibi durumlarda)
    if (error?.message && error?.message.includes('Not Found')) {
      // 404 hatası için özel mesaj
      if (process.client) {
        snackbarStore.setSnackbar({
          severity: 'danger',
          title: {
            langkey: 'errors.field.not_found',
            params: { field: t('fields.slug') },
          },
          timeout: 5000,
          persistent: false,
        })
      }
      return {}
    }

    // HTTP status code kontrolü (fallback)
    if (error?.response?.status === 500 || error?.status === 500) {
      handle500Error()
      return {}
    }

    // Backend'den gelen error response (fallback)
    if (error?.response?.data) {
      return handleApiError(error.response.data)
    }

    // Network veya diğer hatalar
    handle500Error()
    return {}
  }

  return {
    handleApiError,
    handleHttpError,
    handleMessageError,
    handleFieldError,
    handle500Error,
  }
} 